import {booleanAttribute, Component, computed, inject, input, signal} from '@angular/core';
import {MatCard, MatCardContent} from '@angular/material/card';
import {MatIconButton} from '@angular/material/button';
import {VulcanIconComponent} from '@eagle-i/vulcan-component-library';
import {BaseStateReportWidget} from '../base-state-report-widget';
import {rxResource} from '@angular/core/rxjs-interop';
import {RestorationService} from '../../../../../../../services/restoration.service';
import {DecimalPipe} from '@angular/common';
import JSZip from 'jszip';
import html2canvas from 'html2canvas';
import {OrdinalSuffixPipe} from '../../../../../../../shared/pipes/oridnal-suffix.pipe';

@Component({
  selector: 'tgrid-restoration-state-level-metric',
  imports: [
    MatCard,
    MatCardContent,
    MatIconButton,
    VulcanIconComponent,
    DecimalPipe,
    OrdinalSuffixPipe,
  ],
  templateUrl: './restoration-state-level-metric.component.html',
  styleUrl: './restoration-state-level-metric.component.scss',
})
export class RestorationStateLevelMetricComponent extends BaseStateReportWidget {
  /** Earliest year to include when the “all years” flag is active. */
  private readonly START_YEAR = 2018;

  private restorationService = inject(RestorationService);

  /** Input  toggles whether metrics should span every year since 2018. */
  public allYears = input(false, {transform: booleanAttribute});

  protected exporting = signal(false);

  protected availableYears = computed(() => {
    const currentYear = new Date().getFullYear() - 1;
    const count = currentYear - this.START_YEAR + 1;
    return Array.from({length: count}, (_, idx) => this.START_YEAR + idx);
  });

  protected territory = computed(() => {
    return this.territories().find(t => t.abbreviation === this.stateParam());
  });

  protected metricRef = rxResource({
    request: () => {
      const selectedYears = this.allYears() ? this.availableYears() : [this.yearParam()];
      const state = this.territory();
      return ({state, year: selectedYears});
    },
    loader: ({request}) => {
      return this.restorationService.getStateLevelMetrics([request.state!], request.year);
    },
  });

  protected override async export(): Promise<void> {
    this.exporting.set(true);
    await new Promise<void>(resolve => setTimeout(resolve, 100));

    let filename = `state_restoration_stats_${this.stateParam()}`;
    if (this.allYears()) {
      filename += `_${this.START_YEAR}_${new Date().getFullYear()}`;
    } else {
      filename += `_${this.yearParam()}`;
    }
    const zip = new JSZip();
    const element = this.exportContent()?.nativeElement;

    const textContent = element.innerText;
    const textBlob = new Blob([textContent], {type: 'text/plain'});
    zip.file(`${filename}.txt`, textBlob);

    try {
      const canvas = await html2canvas(element);
      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve));
      if (blob) {
        zip.file(`${filename}.png`, blob);
        const content = await zip.generateAsync({type: 'blob'});
        const url = URL.createObjectURL(content);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.zip`;
        link.click();
        URL.revokeObjectURL(url);
      }
      this.snackBar.open('Export downloaded successfully', 'Close', { duration: 3000 });
    } catch (err) {
      console.error(err);
      this.snackBar.open('Export failed. Please try again.', 'Close', { duration: 3000 });
    } finally {
      this.exporting.set(false);
    }
  }

}
